import {
  queryOptions,
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from '@tanstack/react-query';

import { getAuthHeaders } from '@/identity.js';
import { apiErrorAction, apiErrorFromResponse } from '@/lib/api-error.js';
import { notify } from '@/lib/notify.js';

/**
 * Wire types for chat sharing, mirroring the conversation-service `/v1`
 * share contract served through the gateway. The request body is snake_case
 * (`access_mode`); the response ShareView is camelCase — match both sides
 * exactly, the boundary is where names must line up literally.
 */

/**
 * Who may open a shared snapshot. `internal` requires a valid platform JWT
 * (Customertimes employees); `public` serves to anyone with the link.
 */
export type ShareAccessMode = 'internal' | 'public';

/** One active share of a conversation, as returned by create and list. */
export interface ShareView {
  shareId: string;
  conversationId: string;
  accessMode: ShareAccessMode;
  /** Owner-chosen label, or null when the link was created without one. */
  name: string | null;
  isActive: boolean;
  createdAt: string;
  expiresAt: string | null;
}

/**
 * The shares that still open, in the order the service listed them. The dialog counts these and lists
 * these — a revoked link is not a link, and a count that disagreed with the list would be read as the
 * truth, because the count is the half that stays on screen when the list is folded away.
 */
export function activeShares(shares: readonly ShareView[]): ShareView[] {
  return shares.filter((share) => share.isActive);
}

/**
 * One table, chart or diagram this browser drew for the snapshot, as static HTML with inline SVG. A
 * dashboard or diagram preview is named by its index among the message's specs, a ```mermaid block
 * by its source; the service matches each against the stored message before it uses it.
 */
export interface RenderedPart {
  messageId: string;
  kind: 'dashboard' | 'diagram' | 'mermaid';
  index?: number;
  source?: string;
  html: string;
}

/** A part in the snake_case shape the service reads. */
interface RenderedPartBody {
  message_id: string;
  kind: RenderedPart['kind'];
  index?: number;
  source?: string;
  html: string;
}

function toRenderedPartBody(part: RenderedPart): RenderedPartBody {
  return {
    message_id: part.messageId,
    kind: part.kind,
    ...(part.index === undefined ? {} : { index: part.index }),
    ...(part.source === undefined ? {} : { source: part.source }),
    html: part.html,
  };
}

/** The snake_case create body the service expects. */
interface CreateShareBody {
  access_mode: ShareAccessMode;
  /** Optional label; omitted from the body when empty. */
  name?: string;
  /** The drawn parts; omitted when there are none, so the service falls back to text. */
  rendered_parts?: RenderedPartBody[];
}

/** Platform-JWT bearer (zero-trust) + JSON content type for the share endpoints. */
async function authHeaders(): Promise<Record<string, string>> {
  return { 'Content-Type': 'application/json', ...(await getAuthHeaders()) };
}

function sharesEndpoint(conversationId: string): string {
  return `/v1/conversations/${encodeURIComponent(conversationId)}/shares`;
}

async function fetchShares(conversationId: string): Promise<ShareView[]> {
  const headers = await authHeaders();
  const res = await fetch(sharesEndpoint(conversationId), { headers });
  if (!res.ok) {
    const error = await apiErrorFromResponse(res, 'Failed to load shares');
    notify.error(apiErrorAction(error, 'Failed to load shares'));
    throw error;
  }
  return (await res.json()) as ShareView[];
}

/**
 * Create a share for a conversation. The body key is snake_case `access_mode`
 * on the wire — the service's ValidationPipe silently drops anything else, so
 * this exact key is asserted in the tests.
 */
async function createShare(
  conversationId: string,
  accessMode: ShareAccessMode,
  name?: string,
  renderedParts: readonly RenderedPart[] = [],
): Promise<ShareView> {
  const headers = await authHeaders();
  const trimmed = (name ?? '').trim();
  const body: CreateShareBody = { access_mode: accessMode };
  if (trimmed.length > 0) body.name = trimmed;
  if (renderedParts.length > 0) {
    body.rendered_parts = renderedParts.map(toRenderedPartBody);
  }
  const res = await fetch(sharesEndpoint(conversationId), {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const error = await apiErrorFromResponse(
      res,
      'Failed to create the share link',
    );
    notify.error(apiErrorAction(error, 'Failed to create the share link'));
    throw error;
  }
  return (await res.json()) as ShareView;
}

async function revokeShare(
  conversationId: string,
  shareId: string,
): Promise<void> {
  const headers = await authHeaders();
  const res = await fetch(
    `${sharesEndpoint(conversationId)}/${encodeURIComponent(shareId)}`,
    {
      method: 'DELETE',
      headers,
    },
  );
  if (!res.ok && res.status !== 204) {
    const error = await apiErrorFromResponse(
      res,
      'Failed to revoke the share link',
    );
    notify.error(apiErrorAction(error, 'Failed to revoke the share link'));
    throw error;
  }
}

/** TanStack Query factory for a conversation's active shares. */
export function sharesQueryOptions(conversationId: string | null | undefined) {
  return queryOptions({
    queryKey: ['shares', conversationId ?? 'none'],
    queryFn: async (): Promise<ShareView[]> => {
      if (!conversationId) return [];
      return fetchShares(conversationId);
    },
    enabled: Boolean(conversationId),
    staleTime: 30_000,
  });
}

/** The user-facing share URL — an SPA route, not the `/v1` serving endpoint. */
export function shareUrl(shareId: string): string {
  return `${window.location.origin}/share/${encodeURIComponent(shareId)}`;
}

interface CreateShareVars {
  conversationId: string;
  accessMode: ShareAccessMode;
  name?: string;
  renderedParts?: readonly RenderedPart[];
}

export function useCreateShare(): UseMutationResult<
  ShareView,
  Error,
  CreateShareVars
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      conversationId,
      accessMode,
      name,
      renderedParts,
    }: CreateShareVars) =>
      createShare(conversationId, accessMode, name, renderedParts),
    onSuccess: (_share, { conversationId }) => {
      void queryClient.invalidateQueries({
        queryKey: ['shares', conversationId],
      });
    },
  });
}

interface RevokeShareVars {
  conversationId: string;
  shareId: string;
}

export function useRevokeShare(): UseMutationResult<
  void,
  Error,
  RevokeShareVars
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ conversationId, shareId }: RevokeShareVars) =>
      revokeShare(conversationId, shareId),
    onSuccess: (_data, { conversationId }) => {
      void queryClient.invalidateQueries({
        queryKey: ['shares', conversationId],
      });
    },
  });
}
